import React from 'react';
import { shallowEqual } from 'react-redux';
import { TableCell, Typography } from '@mui/material';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { MuiTable, MuiTableColumnItem, MuiTableScrollItems } from 'ui/table/MuiTable';
import { ImportEnrichmentProgressCell } from '../ImportEnrichmentProgressCell';
import { EnrichmentOperation } from '../model/EnrichmentOperation';
import { OperationStatusChip } from '../OperationStatusChip';
import { formatDateTime } from 'lib/timeHelpers';

interface ImportEnrichmentsTableProps {
    operations: EnrichmentOperation[];
    nextKey?: string;
    onScroll: () => void;
    onClick: (item: EnrichmentOperation) => void;
}

export const ImportEnrichmentsTable: React.FC<ImportEnrichmentsTableProps> = ({
    operations,
    nextKey,
    onScroll,
    onClick
}) => {
    const columns = React.useMemo<MuiTableColumnItem[]>(
        () => [
            {
                key: 'createdAt',
                label: 'Date',
                render: (item: EnrichmentOperation) => (
                    <TableCell key={`export-date-${item.nonce}`}>{formatDateTime(item.createdAt)}</TableCell>
                )
            },
            {
                key: 'status',
                label: 'State',
                render: (item: EnrichmentOperation) => (
                    <TableCell key={`export-status-${item.nonce}`} align="center" size="small">
                        <OperationStatusChip status={item.status} />
                    </TableCell>
                ),
                headerProps: {
                    align: 'center'
                }
            },
            {
                key: 'result.message',
                label: 'Message',
                headerProps: {
                    align: 'center'
                },
                render: (item: EnrichmentOperation) => {
                    let message = item.result && item.result.message;
                    const isError = item.status === OperationStatus.ERROR;
                    if (isError) {
                        message = item?.error.message;
                    }
                    return (
                        <TableCell key={`import-message-${item.nonce}`} align="center" size="medium">
                            <Typography align="center" variant="caption">
                                {message}
                            </Typography>
                        </TableCell>
                    );
                }
            },
            {
                key: 'result.errors',
                label: 'Error Count',
                headerProps: {
                    align: 'center'
                },
                render: (item: EnrichmentOperation) => {
                    const errorsCount = item.result && item.result.errors;
                    if (!errorsCount) {
                        return <TableCell key={`import-erros-${item.nonce}`} align="center" size="medium" />;
                    }
                    return (
                        <TableCell key={`import-erros-${item.nonce}`} align="center" size="medium">
                            <Typography align="center" variant="caption">
                                {errorsCount}
                            </Typography>
                        </TableCell>
                    );
                }
            },
            {
                key: 'result.progress',
                label: '',
                headerProps: {
                    align: 'center'
                },
                render: (item: EnrichmentOperation) => (
                    <ImportEnrichmentProgressCell key={`import-action-${item.nonce}`} operation={item} />
                )
            }
        ],
        []
    );
    const scroll = React.useMemo<MuiTableScrollItems>(
        () => ({ isMoreItems: !!nextKey, key: nextKey, onScroll }),
        [onScroll, nextKey]
    );
    const getRowKey = React.useCallback((item: EnrichmentOperation) => item.nonce, []);
    return (
        <MuiTable
            alwaysShowHead
            data={operations}
            scroll={scroll}
            columns={columns}
            getRowKey={getRowKey}
            onClick={onClick}
            isLoading={!!nextKey}
        />
    );
};

export const ImportEnrichmentsTablePure = React.memo(ImportEnrichmentsTable, (prev, next) =>
    shallowEqual(prev, next)
);
